import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Flame, MessageCircle, CalendarClock, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TempDot = ({ t }) => {
  const c = t === 'hot' ? 'bg-rose-500' : t === 'warm' ? 'bg-amber-500' : 'bg-zinc-400';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${c}`} />;
};

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');

export default function HotFollowupsWidget({ token, onOpenLead, refreshKey }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchHot = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/crm/hot-followups?limit=8`, { headers });
      const d = await res.json();
      if (d.success) setItems(d.items || []);
    } catch (e) { console.error('hot-followups', e); }
    finally { setLoading(false); }
  }, [token]);

  const sendInvite = useCallback(async (lead) => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding-schedule/send-invite`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: lead.phone, name: lead.name, lead_id: lead.id }),
      });
      const d = await res.json();
      if (d.success && d.sent) toast.success('Onboarding invite WhatsApp पर भेजा गया!');
      else toast.message('Invite queued — WhatsApp 24h window के बाहर हो सकता है।');
    } catch (e) { toast.error('Invite नहीं भेजा जा सका'); }
  }, [token]);

  const sendDemoInvite = useCallback(async (lead) => {
    try {
      const res = await fetch(`${API_URL}/api/live-chat/send-demo-invite`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: lead.phone, name: lead.name, lead_id: lead.id }),
      });
      const d = await res.json();
      if (d.success && d.sent) toast.success('Demo booking link WhatsApp पर भेज दिया!');
      else if (d.success) toast.message('भेजा नहीं गया — नंबर WhatsApp पर सक्रिय नहीं या template अभी approve नहीं हुआ।');
      else toast.error(d.detail || 'Demo link नहीं भेजा जा सका');
    } catch (e) { toast.error('Demo link नहीं भेजा जा सका'); }
  }, [token]);

  useEffect(() => { fetchHot(); }, [fetchHot, refreshKey]);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="hot-followups-widget">
      <button onClick={() => setOpen(o => !o)} data-testid="hot-followups-toggle"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50">
        <div className="flex items-center gap-2.5">
          <Flame className="w-4 h-4 text-rose-500" strokeWidth={2} />
          <div>
            <p className="font-semibold text-zinc-900 text-sm">आज follow-up करने लायक Hot Leads</p>
            <p className="text-[11px] text-zinc-400 tabular-nums">{items.length} leads · सबसे गरम पहले — कोई lead न छूटे</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-zinc-100 pt-3">
          {loading ? (
            <div className="py-6 text-center"><div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-zinc-200 border-t-zinc-500" /></div>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-zinc-400 text-sm" data-testid="hot-followups-empty">अभी कोई pending hot lead नहीं</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {items.map((l) => {
                const ph = (l.phone || '').replace(/[^0-9]/g, '');
                return (
                  <div key={l.id} className="bg-white rounded-lg p-3 border border-zinc-200 hover:border-zinc-300 flex items-center gap-3 transition-colors" data-testid={`hot-followup-${l.id}`}>
                    <div className="flex flex-col items-center gap-1 w-8">
                      <span className="text-sm font-semibold text-zinc-900 tabular-nums">{l.lead_score}</span>
                      <TempDot t={l.temperature} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-900 truncate">{l.name || 'Lead'}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{l.school_name || l.phone}</p>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">{l.reason} · {inr(l.deal_value)}</span>
                        {l.demo_done && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 font-medium" data-testid={`demo-done-${l.id}`}><span className="w-1 h-1 rounded-full bg-emerald-500" /> Demo हो चुका</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <a href={`https://wa.me/${ph}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`hot-wa-${l.id}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-zinc-200 text-zinc-700 text-center hover:bg-zinc-50"><MessageCircle className="w-3 h-3" /> WA</a>
                      {l.demo_done ? (
                        <button onClick={() => sendInvite(l)} data-testid={`onboarding-invite-${l.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 whitespace-nowrap"><CalendarClock className="w-3 h-3" /> Onboarding</button>
                      ) : (
                        <button onClick={() => sendDemoInvite(l)} data-testid={`demo-invite-${l.id}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 whitespace-nowrap"><CalendarClock className="w-3 h-3" /> Demo link</button>
                      )}
                      <button onClick={() => onOpenLead(l.id)} data-testid={`hot-open-${l.id}`}
                        className="text-[11px] px-2 py-1 rounded-md bg-zinc-900 text-white hover:bg-zinc-800">खोलें</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
